import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { AdvancedSection } from "@/components/ui/advanced-section";
import { formatCurrency, formatDate, formatNumber } from "@/lib/i18n";

interface PlanInfo {
  slug: string;
  name: string;
  tier: number;
  monthlyPriceCents: number;
  annualPriceCents: number;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  modules: string[];
}

interface SubscriptionInfo {
  id: string;
  status: string;
  billingInterval: "monthly" | "annual";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelsAtPeriodEnd: boolean;
  canceledAt: string | null;
  cancelEffectiveAt: string | null;
  dunningAttempts: number;
  trialEndsAt: string | null;
  pendingPlanChange: string | null;
  scheduledChangeAt: string | null;
}

interface CurrentPlanInfo {
  slug: string;
  name: string;
  tier: number;
  monthlyPriceCents: number;
  annualPriceCents: number;
}

interface UsageMeter {
  label: string;
  metric: string;
  used: number;
  limit: number;
  unit: string;
}

interface BillingOverageLine {
  metric: string;
  label: string;
  used: number;
  included: number;
  unitSize: number;
  overageUnits: number;
  overageAmountCents: number;
  resetPeriod: "billing_cycle" | "calendar_month" | "never";
  unitLabel: string;
}

interface BillingPeriod {
  startsAt: string;
  endsAt: string | null;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  issuedAt: string | null;
  dueAt: string | null;
  amountCents: number;
  amountDueCents: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
}

interface InvoiceDetail extends Invoice {
  customerEmail: string;
  subtotalCents: number;
  taxAmountCents: number;
  paidAt: string | null;
  lineItems: Array<{
    description: string;
    quantity: number;
    unitPriceCents: number;
    totalCents: number;
    taxRate: number;
  }>;
}

interface InvoiceComposerState {
  customerName: string;
  customerEmail: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  dueAt: string;
}

interface DunningStatus {
  daysOverdue: number;
  stage: "payment_failed" | "final_warning" | "suspended";
  attempts: number;
  nextEscalationAt: string | null;
}

interface PaymentAnalyticsData {
  windowDays: number;
  summary: {
    mrrCents: number;
    activeSubscriptions: number;
    churnRate: number;
    averageLtvCents: number;
    paymentFailureRate: number;
    totalCustomers: number;
  };
  revenueBySource: Array<{
    source: "subscriptions" | "bookings" | "products";
    label: string;
    amountCents: number;
    share: number;
  }>;
  topCustomers: Array<{
    customerId: string;
    label: string;
    lifetimeValueCents: number;
    paymentCount: number;
  }>;
}

interface BillingTransaction {
  id: string;
  date: string | null;
  customerLabel: string;
  amountCents: number;
  netAmountCents: number;
  currency: string;
  type: "subscription" | "booking" | "invoice" | "product" | "unknown";
  status: string;
  method: string | null;
  description: string | null;
  providerPaymentId: string | null;
}

const PLAN_FEATURES_DISPLAY = [
  { key: "contacts", label: "Contacts" },
  { key: "emails_per_month", label: "Emails/month" },
  { key: "pages", label: "Pages" },
  { key: "team_members", label: "Team members" },
  { key: "automations", label: "Automations" },
  { key: "custom_domain", label: "Custom domain" },
  { key: "white_label", label: "White label" },
  { key: "ai_credits_per_month", label: "AI credits/month" },
  { key: "priority_support", label: "Priority support" },
];

function getMeterPercent(meter: UsageMeter): number {
  if (meter.limit <= 0) return 0;
  return Math.min((meter.used / meter.limit) * 100, 100);
}

function getDunningTone(stage: DunningStatus["stage"]): string {
  if (stage === "suspended") return "Service access is suspended until payment is updated.";
  if (stage === "final_warning") return "Final warning: update your payment method before service interruption.";
  return "Stripe reported a failed payment. Update your payment method to avoid disruption.";
}

function formatPercent(value: number): string {
  return `${value.toFixed(value % 1 === 0 ? 0 : 2)}%`;
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export default function BillingPage() {
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [exportingTransactions, setExportingTransactions] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("all");
  const [transactionFrom, setTransactionFrom] = useState(() => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 30);
    return toDateInputValue(date);
  });
  const [transactionTo, setTransactionTo] = useState(() => toDateInputValue(new Date()));
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [currentPlan, setCurrentPlan] = useState<CurrentPlanInfo | null>(null);
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [usage, setUsage] = useState<UsageMeter[]>([]);
  const [billableOverages, setBillableOverages] = useState<BillingOverageLine[]>([]);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [dunning, setDunning] = useState<DunningStatus | null>(null);
  const [analytics, setAnalytics] = useState<PaymentAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planActionInFlight, setPlanActionInFlight] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
  const [showInvoiceComposer, setShowInvoiceComposer] = useState(false);
  const [invoiceComposerMode, setInvoiceComposerMode] = useState<"create" | "edit">("create");
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<string | null>(null);
  const [invoiceForm, setInvoiceForm] = useState<InvoiceComposerState>({
    customerName: "",
    customerEmail: "",
    description: "",
    quantity: 1,
    unitPriceCents: 0,
    dueAt: "",
  });

  const loadInvoices = useCallback(async () => {
    const invoiceRes = await apiFetch<{ invoices: Invoice[] }>("/v1/billing/invoices");
    setInvoices(invoiceRes.invoices);
  }, []);

  const loadInvoiceDetail = useCallback(async (invoiceId: string) => {
    const detailRes = await apiFetch<{ data: InvoiceDetail }>(`/v1/billing/invoices/${invoiceId}`);
    setInvoiceDetail(detailRes.data);
  }, []);

  const loadTransactions = useCallback(async () => {
    setTransactionsLoading(true);

    try {
      const params = new URLSearchParams();
      if (transactionFrom) {
        params.set("from", new Date(`${transactionFrom}T00:00:00.000Z`).toISOString());
      }
      if (transactionTo) {
        params.set("to", new Date(`${transactionTo}T23:59:59.999Z`).toISOString());
      }
      if (transactionStatus !== "all") {
        params.set("status", transactionStatus);
      }

      const query = params.toString();
      const response = await apiFetch<{ data: BillingTransaction[] }>(
        `/v1/billing/transactions${query ? `?${query}` : ""}`,
      );
      setTransactions(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setTransactionsLoading(false);
    }
  }, [transactionFrom, transactionStatus, transactionTo]);

  const handleExportTransactions = useCallback(async () => {
    setExportingTransactions(true);

    try {
      const params = new URLSearchParams({ format: "csv" });
      if (transactionFrom) {
        params.set("from", new Date(`${transactionFrom}T00:00:00.000Z`).toISOString());
      }
      if (transactionTo) {
        params.set("to", new Date(`${transactionTo}T23:59:59.999Z`).toISOString());
      }

      const response = await fetch(`/v1/billing/export?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `billing-export-${transactionFrom}-${transactionTo}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export billing data");
    } finally {
      setExportingTransactions(false);
    }
  }, [transactionFrom, transactionTo]);

  useEffect(() => {
    async function load() {
      try {
        const [
          subscriptionRes,
          plansRes,
          usageRes,
          dunningRes,
          analyticsRes,
        ] = await Promise.all([
          apiFetch<{ subscription: SubscriptionInfo; plan: CurrentPlanInfo }>("/v1/billing/subscription"),
          apiFetch<{ plans: PlanInfo[] }>("/v1/billing/plans"),
          apiFetch<{
            usage: UsageMeter[];
            billableOverages: BillingOverageLine[];
            billingPeriod: BillingPeriod;
          }>("/v1/billing/usage"),
          apiFetch<{ data: DunningStatus | null }>("/v1/billing/dunning-status"),
          apiFetch<{ data: PaymentAnalyticsData }>("/v1/billing/analytics"),
        ]);

        setSubscription(subscriptionRes.subscription);
        setCurrentPlan(subscriptionRes.plan);
        setPlans(plansRes.plans);
        setUsage(usageRes.usage);
        setBillableOverages(usageRes.billableOverages);
        setBillingPeriod(usageRes.billingPeriod);
        setDunning(dunningRes.data);
        setAnalytics(analyticsRes.data);
        await loadInvoices();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load billing data");
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [loadInvoices]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  const resetInvoiceComposer = useCallback(() => {
    setInvoiceForm({
      customerName: "",
      customerEmail: "",
      description: "",
      quantity: 1,
      unitPriceCents: 0,
      dueAt: "",
    });
    setInvoiceComposerMode("create");
    setShowInvoiceComposer(false);
  }, []);

  const openCreateInvoice = useCallback(() => {
    setInvoiceComposerMode("create");
    setInvoiceForm({
      customerName: "",
      customerEmail: "",
      description: "",
      quantity: 1,
      unitPriceCents: 0,
      dueAt: "",
    });
    setShowInvoiceComposer(true);
  }, []);

  const openEditInvoice = useCallback(() => {
    if (!invoiceDetail) return;
    const firstLine = invoiceDetail.lineItems[0];
    setInvoiceComposerMode("edit");
    setInvoiceForm({
      customerName: invoiceDetail.customerName,
      customerEmail: invoiceDetail.customerEmail,
      description: firstLine?.description ?? "",
      quantity: firstLine?.quantity ?? 1,
      unitPriceCents: firstLine?.unitPriceCents ?? 0,
      dueAt: invoiceDetail.dueAt ? invoiceDetail.dueAt.slice(0, 10) : "",
    });
    setShowInvoiceComposer(true);
  }, [invoiceDetail]);

  const handlePlanAction = useCallback(async (planSlug: string) => {
    const interval = subscription?.billingInterval ?? "monthly";
    setPlanActionInFlight(planSlug);

    try {
      if (subscription && currentPlan && currentPlan.slug !== "free") {
        await apiFetch("/v1/billing/change-plan", {
          method: "POST",
          body: JSON.stringify({ newPlanKey: planSlug }),
        });

        const targetPlan = plans.find((plan) => plan.slug === planSlug);
        if (targetPlan) {
          setCurrentPlan({
            slug: targetPlan.slug,
            name: targetPlan.name,
            tier: targetPlan.tier,
            monthlyPriceCents: targetPlan.monthlyPriceCents,
            annualPriceCents: targetPlan.annualPriceCents,
          });
          setSubscription((previous) => (
            previous
              ? {
                  ...previous,
                  pendingPlanChange: targetPlan.slug,
                }
              : previous
          ));
        }
        return;
      }

      const result = await apiFetch<{ checkoutUrl: string }>("/v1/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ planKey: planSlug, billingInterval: interval }),
      });

      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Plan change failed");
    } finally {
      setPlanActionInFlight(null);
    }
  }, [currentPlan, plans, subscription]);

  const handleCancel = useCallback(async () => {
    if (!window.confirm("Cancel at period end? Premium features remain active until the current billing period closes.")) {
      return;
    }

    try {
      await apiFetch("/v1/billing/cancel", { method: "POST" });
      setSubscription((previous) => (
        previous
          ? { ...previous, cancelsAtPeriodEnd: true }
          : previous
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cancellation failed");
    }
  }, []);

  const handleInvoiceSubmit = useCallback(async () => {
    try {
      setInvoiceActionLoading(invoiceComposerMode);
      const payload = {
        customerName: invoiceForm.customerName,
        customerEmail: invoiceForm.customerEmail,
        dueAt: invoiceForm.dueAt ? new Date(`${invoiceForm.dueAt}T00:00:00.000Z`).toISOString() : null,
        currencyCode: currentPlan?.slug ? "eur" : "eur",
        lineItems: [{
          description: invoiceForm.description,
          quantity: invoiceForm.quantity,
          unitPriceCents: invoiceForm.unitPriceCents,
          totalCents: invoiceForm.quantity * invoiceForm.unitPriceCents,
          taxRate: 0,
        }],
      };

      if (invoiceComposerMode === "create") {
        const response = await apiFetch<{ data: InvoiceDetail }>("/v1/billing/invoices", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        await loadInvoices();
        setInvoiceDetail(response.data);
      } else if (invoiceDetail) {
        const response = await apiFetch<{ data: InvoiceDetail }>(`/v1/billing/invoices/${invoiceDetail.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        await loadInvoices();
        setInvoiceDetail(response.data);
      }

      resetInvoiceComposer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invoice save failed");
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [currentPlan?.slug, invoiceComposerMode, invoiceDetail, invoiceForm, loadInvoices, resetInvoiceComposer]);

  const handleSendInvoice = useCallback(async (invoiceId: string) => {
    try {
      setInvoiceActionLoading(`send-${invoiceId}`);
      await apiFetch<{ data: InvoiceDetail }>(`/v1/billing/invoices/${invoiceId}/send`, { method: "POST" });
      await loadInvoices();
      await loadInvoiceDetail(invoiceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invoice send failed");
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [loadInvoiceDetail, loadInvoices]);

  const handleMarkPaid = useCallback(async (invoiceId: string) => {
    try {
      setInvoiceActionLoading(`paid-${invoiceId}`);
      await apiFetch<{ data: InvoiceDetail }>(`/v1/billing/invoices/${invoiceId}/mark-paid`, { method: "POST" });
      await loadInvoices();
      await loadInvoiceDetail(invoiceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mark paid failed");
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [loadInvoiceDetail, loadInvoices]);

  const handleOpenPortal = useCallback(async () => {
    setOpeningPortal(true);
    try {
      const result = await apiFetch<{ url: string }>("/v1/billing/portal", { method: "POST" });
      window.location.href = result.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open Stripe portal");
    } finally {
      setOpeningPortal(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border p-4 space-y-3">
        <div className="h-6 w-48 rounded bg-[var(--color-muted)]/60" />
        <div className="h-20 rounded bg-[var(--color-muted)]/40" />
        <div className="h-20 rounded bg-[var(--color-muted)]/40" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      {error && (
        <div className="rounded-md bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {dunning && (
        <section className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[var(--color-error)]">
                Billing alert
              </p>
              <h2 className="mt-1 text-lg font-semibold text-foreground">
                {dunning.stage === "suspended" ? "Account suspended for failed payment" : "Payment recovery in progress"}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {getDunningTone(dunning.stage)}
              </p>
            </div>
            <div className="rounded-lg border border-[var(--color-error)]/15 bg-background/80 px-4 py-3 text-sm">
              <div className="font-medium text-foreground">
                {dunning.daysOverdue} day{dunning.daysOverdue === 1 ? "" : "s"} overdue
              </div>
              <div className="mt-1 text-muted-foreground">
                Attempt {dunning.attempts}
                {dunning.nextEscalationAt ? ` · Next escalation ${formatDate(dunning.nextEscalationAt)}` : ""}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="stagger-item">
        <h2 className="text-lg font-semibold text-foreground">Current plan</h2>
        <div className="mt-4 rounded-lg border border-border p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xl font-bold text-foreground">
                {currentPlan?.name ?? "Free"}
                {subscription?.status === "trialing" && (
                  <span className="ml-2 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-primary)]">
                    Trial
                  </span>
                )}
                {subscription?.cancelsAtPeriodEnd && (
                  <span className="ml-2 rounded-full bg-[var(--color-accent-action-light)] px-2 py-0.5 text-xs font-medium text-orange-700">
                    Cancelling
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {subscription?.billingInterval === "annual" ? "Annual" : "Monthly"} billing
                {subscription?.currentPeriodEnd ? ` · Renews ${formatDate(subscription.currentPeriodEnd)}` : ""}
              </p>
              {subscription?.trialEndsAt && (
                <p className="mt-1 text-sm text-[var(--color-primary)]">
                  Trial ends {formatDate(subscription.trialEndsAt)}
                </p>
              )}
              {subscription?.pendingPlanChange && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Scheduled change to <span className="font-medium text-foreground">{subscription.pendingPlanChange}</span>
                  {subscription.scheduledChangeAt ? ` on ${formatDate(subscription.scheduledChangeAt)}` : ""}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {subscription && !subscription.cancelsAtPeriodEnd && currentPlan?.slug !== "free" && (
                <button
                  onClick={handleCancel}
                  className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-accent"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {plans.length > 0 && (
        <section className="stagger-item">
          <AdvancedSection
            title="Advanced plan changes"
            description="Compare limits, modules, and billing impact before you change the workspace plan."
          >
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.filter((plan) => plan.slug !== "free" || !subscription).map((plan) => {
                const isCurrent = plan.slug === currentPlan?.slug;
                const currentTier = currentPlan?.tier ?? 0;
                const isUpgrade = plan.tier > currentTier;
                const displayPriceCents = subscription?.billingInterval === "annual"
                  ? plan.annualPriceCents
                  : plan.monthlyPriceCents;

                return (
                  <div
                    key={plan.slug}
                    className={`rounded-lg border p-6 ${isCurrent ? "border-primary bg-primary/5" : "border-border"}`}
                  >
                    <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                    <p className="mt-1 text-2xl font-bold text-foreground">
                      €{(displayPriceCents / 100).toFixed(0)}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{subscription?.billingInterval === "annual" ? "yr" : "mo"}
                      </span>
                    </p>
                    {plan.annualPriceCents > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Monthly {formatCurrency(plan.monthlyPriceCents, "eur")} · Annual {formatCurrency(plan.annualPriceCents, "eur")}
                      </p>
                    )}

                    <ul className="mt-4 space-y-2">
                      {PLAN_FEATURES_DISPLAY.map((feature) => {
                        const limit = plan.limits[feature.key];
                        const enabled = plan.features[feature.key];
                        const value = limit !== undefined
                          ? (limit === -1 ? "Unlimited" : String(limit))
                          : enabled ? "Included" : "—";

                        return (
                          <li key={feature.key} className="flex items-center gap-2 text-sm">
                            <span className={value === "—" ? "text-muted-foreground" : "text-foreground"}>
                              {value}
                            </span>
                            <span className="text-muted-foreground">{feature.label}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <button
                      onClick={() => {
                        if (!isCurrent) {
                          void handlePlanAction(plan.slug);
                        }
                      }}
                      disabled={isCurrent || planActionInFlight !== null}
                      className={`mt-6 w-full rounded-md px-4 py-2 text-sm font-medium ${
                        isCurrent
                          ? "cursor-default bg-muted text-muted-foreground"
                          : isUpgrade
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : "border border-border text-foreground hover:bg-accent"
                      } disabled:opacity-50`}
                    >
                      {isCurrent
                        ? "Current plan"
                        : planActionInFlight === plan.slug
                          ? "Applying…"
                          : isUpgrade ? "Upgrade" : "Change plan"}
                    </button>
                  </div>
                );
              })}
            </div>
          </AdvancedSection>
        </section>
      )}

      <section className="stagger-item">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Payment analytics</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Current recurring revenue plus the last {analytics?.windowDays ?? 30} days of retention and payment evidence.
            </p>
          </div>
        </div>

        {analytics ? (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">MRR</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrency(analytics.summary.mrrCents, "eur")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{analytics.summary.activeSubscriptions} active subscription{analytics.summary.activeSubscriptions === 1 ? "" : "s"}</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Churn rate</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatPercent(analytics.summary.churnRate)}</p>
                <p className="mt-1 text-sm text-muted-foreground">Trailing {analytics.windowDays}-day subscription churn</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Average LTV</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrency(analytics.summary.averageLtvCents, "eur")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{analytics.summary.totalCustomers} paying customer{analytics.summary.totalCustomers === 1 ? "" : "s"} in ledger</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Payment failure rate</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatPercent(analytics.summary.paymentFailureRate)}</p>
                <p className="mt-1 text-sm text-muted-foreground">Failed versus recovered payment attempts in-window</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Revenue by source</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Cash collected over the last {analytics.windowDays} days across subscription, booking, and one-off product flows.</p>
                  </div>
                </div>
                <div className="mt-4 space-y-4">
                  {analytics.revenueBySource.map((source) => (
                    <div key={source.source}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{source.label}</span>
                        <span className="text-muted-foreground">{formatCurrency(source.amountCents, "eur")} · {formatPercent(source.share)}</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(source.share, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">Top customers by lifetime value</h3>
                <div className="mt-4 space-y-3">
                  {analytics.topCustomers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No completed payment history is available yet.</p>
                  ) : analytics.topCustomers.map((customer) => (
                    <div key={customer.customerId} className="rounded-md border border-border px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-foreground">{customer.label}</p>
                          <p className="text-sm text-muted-foreground">{customer.paymentCount} payment{customer.paymentCount === 1 ? "" : "s"}</p>
                        </div>
                        <p className="font-medium text-foreground">{formatCurrency(customer.lifetimeValueCents, "eur")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-border p-4 text-sm text-muted-foreground">
            Payment analytics will appear once subscription and payment ledgers have enough mounted data to summarize.
          </div>
        )}
      </section>

      <section className="stagger-item">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Usage</h2>
            {billingPeriod && (
              <p className="mt-1 text-sm text-muted-foreground">
                Billing period {formatDate(billingPeriod.startsAt)}{billingPeriod.endsAt ? ` to ${formatDate(billingPeriod.endsAt)}` : ""}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {usage.map((meter) => {
            const percent = getMeterPercent(meter);
            const isWarning = percent >= 80;
            const isExceeded = percent >= 100;

            return (
              <div key={meter.metric}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{meter.label}</span>
                  <span className={isExceeded ? "font-medium text-[var(--color-destructive)]" : isWarning ? "text-[var(--color-accent-action)]" : "text-muted-foreground"}>
                    {formatNumber(meter.used)} / {meter.limit === -1 ? "∞" : formatNumber(meter.limit)}{meter.unit}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full transition-all ${isExceeded ? "bg-[var(--color-destructive)]/50" : isWarning ? "bg-[var(--color-accent-action)]" : "bg-primary"}`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            );
          })}
          {usage.length === 0 && (
            <div className="rounded-md border border-border p-4 text-sm text-muted-foreground">
              No billable usage recorded for the current period.
            </div>
          )}
        </div>
      </section>

      <section className="stagger-item">
        <h2 className="text-lg font-semibold text-foreground">Billable overages</h2>
        <div className="mt-4 rounded-lg border border-border">
          {billableOverages.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No overages are accruing in the current billing period.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {billableOverages.map((overage) => (
                <div key={overage.metric} className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{overage.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatNumber(overage.used)} used · {formatNumber(overage.included)} included · {formatNumber(overage.overageUnits)} overage unit{overage.overageUnits === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-foreground">{formatCurrency(overage.overageAmountCents, "eur")}</p>
                    <p className="text-xs text-muted-foreground">
                      per {formatNumber(overage.unitSize)} {overage.unitLabel}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="stagger-item">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Transactions</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Mounted payment ledger activity for billing, bookings, and one-off charges.
            </p>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="grid gap-3 md:grid-cols-3">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">From</span>
              <input
                type="date"
                value={transactionFrom}
                onChange={(event) => setTransactionFrom(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">To</span>
              <input
                type="date"
                value={transactionTo}
                onChange={(event) => setTransactionTo(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Status</span>
              <select
                value={transactionStatus}
                onChange={(event) => setTransactionStatus(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground"
              >
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="partially_refunded">Partially refunded</option>
                <option value="refunded">Refunded</option>
              </select>
            </label>
            </div>
            <button
              type="button"
              onClick={() => void handleExportTransactions()}
              disabled={exportingTransactions}
              className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-accent disabled:opacity-50"
            >
              {exportingTransactions ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border">
          {transactionsLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading transactions…</div>
          ) : transactions.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No transactions match the current filters.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left text-sm text-muted-foreground">
                  <th className="pb-2 pl-4 font-medium">Date</th>
                  <th className="pb-2 font-medium">Customer</th>
                  <th className="pb-2 font-medium">Amount</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 pr-4 font-medium">Method</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-border text-sm last:border-b-0">
                    <td className="py-3 pl-4 text-foreground">{transaction.date ? formatDate(transaction.date) : "—"}</td>
                    <td className="py-3 text-foreground">
                      <div>{transaction.customerLabel}</div>
                      {transaction.description ? (
                        <div className="text-xs text-muted-foreground">{transaction.description}</div>
                      ) : null}
                    </td>
                    <td className="py-3 text-foreground">
                      <div>{formatCurrency(transaction.amountCents, transaction.currency)}</div>
                      {transaction.netAmountCents !== transaction.amountCents ? (
                        <div className="text-xs text-muted-foreground">
                          Net {formatCurrency(transaction.netAmountCents, transaction.currency)}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 capitalize text-foreground">{transaction.type}</td>
                    <td className="py-3">
                      <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                        {transaction.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-foreground">{transaction.method ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="stagger-item">
        <h2 className="text-lg font-semibold text-foreground">Payment method</h2>
        <div className="mt-4 rounded-md border border-border p-4">
          {currentPlan?.slug !== "free" ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground">Manage saved payment methods, invoices, and tax details in Stripe.</p>
                <p className="mt-1 text-xs text-muted-foreground">This opens the hosted billing portal for the current subscription.</p>
              </div>
              <button
                onClick={() => void handleOpenPortal()}
                disabled={openingPortal}
                className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent disabled:opacity-50"
              >
                {openingPortal ? "Opening…" : "Manage in Stripe"}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">No payment method on file because this workspace is on the free plan.</p>
              <button
                onClick={() => void handlePlanAction(plans.find((plan) => plan.tier === 1)?.slug ?? "starter")}
                className="mt-3 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
              >
                Add payment method
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="stagger-item">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-foreground">Invoices</h2>
          <button
            type="button"
            onClick={openCreateInvoice}
            className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
          >
            New draft invoice
          </button>
        </div>
        <div className="mt-4">
          {invoices.length === 0 ? (
            <div className="rounded-md border border-border p-4">
              <p className="text-sm text-muted-foreground">No invoices yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left text-sm text-muted-foreground">
                  <th className="pb-2 font-medium">Invoice</th>
                  <th className="pb-2 font-medium">Customer</th>
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Amount</th>
                  <th className="pb-2 font-medium">Due</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-border text-sm">
                    <td className="py-3 text-foreground font-mono text-xs">{invoice.invoiceNumber}</td>
                    <td className="py-3 text-foreground">{invoice.customerName}</td>
                    <td className="py-3 text-foreground">{invoice.issuedAt ? formatDate(invoice.issuedAt) : "—"}</td>
                    <td className="py-3 text-foreground">{formatCurrency(invoice.amountDueCents, invoice.currency)}</td>
                    <td className="py-3 text-foreground">{invoice.dueAt ? formatDate(invoice.dueAt) : "—"}</td>
                    <td className="py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        invoice.status === "paid"
                          ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                          : invoice.status === "open"
                            ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                            : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        onClick={() => void loadInvoiceDetail(invoice.id)}
                        className="text-primary hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {showInvoiceComposer ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xl rounded-xl border border-border bg-background p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {invoiceComposerMode === "create" ? "Create draft invoice" : "Edit draft invoice"}
              </h3>
              <button type="button" onClick={resetInvoiceComposer} className="text-sm text-muted-foreground hover:text-foreground">
                Close
              </button>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Customer name</span>
                <input value={invoiceForm.customerName} onChange={(event) => setInvoiceForm((current) => ({ ...current, customerName: event.target.value }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Customer email</span>
                <input value={invoiceForm.customerEmail} onChange={(event) => setInvoiceForm((current) => ({ ...current, customerEmail: event.target.value }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="text-muted-foreground">Line item description</span>
                <input value={invoiceForm.description} onChange={(event) => setInvoiceForm((current) => ({ ...current, description: event.target.value }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Quantity</span>
                <input type="number" min={1} value={invoiceForm.quantity} onChange={(event) => setInvoiceForm((current) => ({ ...current, quantity: Number(event.target.value) || 1 }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Unit price (cents)</span>
                <input type="number" min={0} value={invoiceForm.unitPriceCents} onChange={(event) => setInvoiceForm((current) => ({ ...current, unitPriceCents: Number(event.target.value) || 0 }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="text-muted-foreground">Due date</span>
                <input type="date" value={invoiceForm.dueAt} onChange={(event) => setInvoiceForm((current) => ({ ...current, dueAt: event.target.value }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground" />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={resetInvoiceComposer} className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent">
                Cancel
              </button>
              <button type="button" onClick={() => void handleInvoiceSubmit()} disabled={invoiceActionLoading === invoiceComposerMode} className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50">
                {invoiceActionLoading === invoiceComposerMode ? "Saving…" : invoiceComposerMode === "create" ? "Create draft" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {invoiceDetail ? (
        <div className="fixed inset-0 z-40 flex items-center justify-end bg-black/20" onClick={() => setInvoiceDetail(null)}>
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-border bg-background p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-xs text-muted-foreground">{invoiceDetail.invoiceNumber}</p>
                <h3 className="text-lg font-semibold text-foreground">{invoiceDetail.customerName}</h3>
                <p className="text-sm text-muted-foreground">{invoiceDetail.customerEmail}</p>
              </div>
              <button type="button" onClick={() => setInvoiceDetail(null)} className="text-sm text-muted-foreground hover:text-foreground">
                Close
              </button>
            </div>

            <div className="mt-6 rounded-md border border-border p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium text-foreground">{invoiceDetail.status}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Amount due</span>
                <span className="font-medium text-foreground">{formatCurrency(invoiceDetail.amountDueCents, invoiceDetail.currency)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Due date</span>
                <span className="font-medium text-foreground">{invoiceDetail.dueAt ? formatDate(invoiceDetail.dueAt) : "Due on receipt"}</span>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <h4 className="text-sm font-medium text-foreground">Line items</h4>
              {invoiceDetail.lineItems.map((item, index) => (
                <div key={`${invoiceDetail.id}-${index}`} className="rounded-md border border-border p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-foreground">{item.description}</span>
                    <span className="text-foreground">{formatCurrency(item.totalCents, invoiceDetail.currency)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.quantity} × {formatCurrency(item.unitPriceCents, invoiceDetail.currency)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {invoiceDetail.status === "draft" ? (
                <button type="button" onClick={openEditInvoice} className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent">
                  Edit draft
                </button>
              ) : null}
              {invoiceDetail.status !== "paid" ? (
                <button type="button" onClick={() => void handleSendInvoice(invoiceDetail.id)} disabled={invoiceActionLoading === `send-${invoiceDetail.id}`} className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50">
                  {invoiceActionLoading === `send-${invoiceDetail.id}` ? "Sending…" : "Send invoice"}
                </button>
              ) : null}
              {invoiceDetail.status !== "paid" ? (
                <button type="button" onClick={() => void handleMarkPaid(invoiceDetail.id)} disabled={invoiceActionLoading === `paid-${invoiceDetail.id}`} className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent disabled:opacity-50">
                  {invoiceActionLoading === `paid-${invoiceDetail.id}` ? "Marking…" : "Mark paid"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
